<script setup lang="ts">
import { prefersReducedMotion } from '@consumer/helpers/screen'
import { Rive as RiveInstance, RiveFile, Layout, Fit } from '@rive-app/canvas'
import Rive from '@shared/components/Rive/canvas/Rive.vue'
import { isTest } from '@shared/environment'

const {
  riveUrl,
  riveFile,
  layout = new Layout({ fit: Fit.Layout }),
  stateMachine,
  artboard,
  loadingText,
  autoBind,
  fallbackImageUrl,
  href,
} = defineProps<{
  riveUrl: string
  riveFile?: RiveFile
  loadingIndicator?: boolean
  loadingText?: string
  layout?: Layout
  stateMachine: string
  artboard: string
  autoBind?: boolean
  fallbackImageUrl?: string
  href?: string
}>()

const emit = defineEmits<{ loaded: [RiveInstance, RiveFile]; beforePlay: [] }>()
let riveInstance = $shallowRef<RiveInstance | null>(null)
const stateMachines = [stateMachine]
const isSSR = import.meta.env.SSR

function onRiveLoaded (r: RiveInstance, f: RiveFile) {
  riveInstance = r
  emit('loaded', r, f)
}

function restart () {
  riveInstance?.reset({
    artboard,
    stateMachines,
    autoplay: true,
    autoBind,
  })
  emit('beforePlay')
  riveInstance?.play()
}

function pause () {
  if (riveInstance) riveInstance.pause()
}

function play () {
  if (riveInstance) {
    emit('beforePlay')
    riveInstance.play()
  }
}

defineExpose({
  play,
  pause,
  restart,
})
</script>

<template>
  <div v-if="isSSR || isTest" class="relative w-full h-full">
    <a v-if="href" :href="href" class="absolute w-full h-full">
      <slot name="ssr"/>
    </a>
    <slot v-else name="ssr"/>
  </div>
  <div v-else-if="prefersReducedMotion()" class="relative w-full h-full">
    <a v-if="href" :href="href" class="absolute w-full h-full">
      <slot name="fallback"/>
    </a>
    <slot v-else name="fallback"/>
  </div>
  <Rive
    v-else
    :riveFile="riveFile"
    :src="riveUrl"
    autoplay
    :autoBind="autoBind"
    :stateMachines="stateMachines"
    :artboard="artboard"
    :loadingText="loadingText"
    :layout="layout"
    class="w-full h-full"
    :loadingIndicator="loadingIndicator"
    :href="href"
    @loaded="onRiveLoaded"
  >
    <template v-if="fallbackImageUrl || $slots.loading" #loading="{ loading }">
      <template v-if="loading">
        <img
          v-if="fallbackImageUrl"
          :src="fallbackImageUrl"
          alt="Rive Animation Fallback"
          class="w-full h-full object-cover"
        >
        <slot v-else name="loading"/>
      </template>
    </template>
    <template v-if="fallbackImageUrl || $slots.loadFailed || $slots.fallback" #loadFailed="{ loadFailed }">
      <template v-if="loadFailed">
        <img
          v-if="fallbackImageUrl"
          :src="fallbackImageUrl"
          alt="Rive Animation Fallback"
          class="w-full h-full object-contain"
        >
        <slot v-else name="loadFailed"/>
        <slot v-if="!$slots.loadFailed && !fallbackImageUrl" name="fallback"/>
      </template>
    </template>
  </Rive>
</template>
